import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  FG110M,
  FG111M,
  FG112,
  FG113M,
  FG114,
  FG115,
  pwg201Api,
  ValidationError,
} from '../../../services/pwg201Api';

const emptyMaster = (): FG110M => ({
  fg110_01: '*.*.*.',
  fg110_11: new Date().toISOString().substring(0, 10),
  fg110_12: 0,
  fg110_13: 0,
  fg110_14: 'N',
  fg110_15: '',
  fg110_16: 0,
  fg110_17: '1',
  fg110_18: '',
  fg110_19: '',
  fg110_20: '',
  fg110_21: 0,
  fg110_211: 0,
  fg110_212: 0,
  fg110_214: 0,
  fg110_215: 0,
  fg110_22: 0,
  fg110_23: 0,
  fg110_24: 1,
  fg110_25: 'NTD',
  fg110_26: '',
  fg110_27: '',
  fg110_28: '01',
  fg110_29: '08',
  fg110_30: 0,
  fg110_32: null,
  fg110_80: 'N',
  fg110_801: '',
  fg110_802: null,
  fg110_803: '',
  fg110_81: '',
  fg110_811: '',
  fg110_812: null,
  fg110_813: '',
  fg110_90: 0,
  fg110_91: '',
  fg110_92: null,
  fg110_93: '',
});

export function validatePWG201Client(
  master: FG110M,
  details: FG111M[],
  writeoffBorr: FG113M[],
  writeoffAdv2: FG114[],
  allocFee: FG115[],
): ValidationError[] {
  const errors: ValidationError[] = [];
  const required = (field: keyof FG110M, value: unknown) => {
    if (!String(value ?? '').trim()) errors.push({ field: String(field), message: `${String(field)} is required` });
  };
  required('fg110_17', master.fg110_17);
  required('fg110_11', master.fg110_11);
  required('fg110_25', master.fg110_25);
  required('fg110_28', master.fg110_28);
  required('fg110_29', master.fg110_29);
  required('fg110_26', master.fg110_26);
  required('fg110_19', master.fg110_19);
  if (master.fg110_25 === 'USD' && master.fg110_24 === 1) errors.push({ field: 'fg110_24', message: 'USD exchange rate cannot be 1' });
  if (details.length === 0) errors.push({ field: 'details', message: 'details is required' });
  if (master.fg110_17 === '5' && master.fg110_30 === 0) errors.push({ field: 'fg110_30', message: 'document type is required' });
  if (master.fg110_17 === '9' && master.fg110_29 === '14' && writeoffBorr.length === 0) errors.push({ field: 'writeoff_borr', message: 'writeoff_borr is required' });
  if (master.fg110_17 === '9' && master.fg110_29 === '19' && writeoffAdv2.length === 0) errors.push({ field: 'writeoff_adv2', message: 'writeoff_adv2 is required' });
  const allocated = Math.round(allocFee.reduce((sum, row) => sum + Number(row.fg115_12 || 0), 0) * 100) / 100;
  if (allocated !== Math.round(Number(master.fg110_16 || 0) * 100) / 100) errors.push({ field: 'alloc_fee', message: 'allocated fee total mismatch' });
  return errors;
}

export function usePWG201Record() {
  const [record, setRecord] = useState<FG110M | null>(null);
  const [lastApiResponse, setLastApiResponse] = useState<FG110M | null>(null);
  const [formData, setFormData] = useState<FG110M>(emptyMaster());
  const [details, setDetails] = useState<FG111M[]>([]);
  const [writeoffAdv, setWriteoffAdv] = useState<FG112[]>([]);
  const [writeoffBorr, setWriteoffBorr] = useState<FG113M[]>([]);
  const [writeoffAdv2, setWriteoffAdv2] = useState<FG114[]>([]);
  const [allocFee, setAllocFee] = useState<FG115[]>([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const rec = await pwg201Api.get(id);
      setRecord(rec);
      setLastApiResponse(rec);
      setFormData({ ...rec });
      setDetails(rec.details ?? []);
      setWriteoffAdv(rec.writeoff_adv ?? []);
      setWriteoffBorr(rec.writeoff_borr ?? []);
      setWriteoffAdv2(rec.writeoff_adv2 ?? []);
      setAllocFee(rec.alloc_fee ?? []);
    } catch (err) {
      message.error('載入繳款單失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const resetForAdd = useCallback(() => {
    const blank = emptyMaster();
    setRecord(null);
    setLastApiResponse(null);
    setFormData(blank);
    setDetails([]);
    setWriteoffAdv([]);
    setWriteoffBorr([]);
    setWriteoffAdv2([]);
    setAllocFee([]);
    setValidationErrors([]);
  }, []);

  const loadAcceptanceDraft = useCallback((sample: FG110M) => {
    const draft: FG110M = {
      ...sample,
      fg110_01: '*.*.*.',
      fg110_11: new Date().toISOString().substring(0, 10),
      fg110_14: 'N',
      fg110_32: null,
      fg110_80: 'N',
      fg110_801: '',
      fg110_802: null,
      fg110_803: '',
      fg110_81: '',
      fg110_811: '',
      fg110_812: null,
      fg110_813: '',
      fg110_90: 0,
      fg110_91: '',
      fg110_92: null,
      fg110_93: '',
    };
    const draftDetails = (sample.details ?? []).map((row, index) => ({
      ...row,
      fg111_01: '',
      fg111_02: index + 1,
      fg111_90: 0,
    }));
    const draftWriteoffAdv = (sample.writeoff_adv ?? []).map((row, index) => ({
      ...row,
      fg112_01: '',
      fg112_02: index + 1,
      fg112_90: 0,
    }));
    const draftWriteoffBorr = (sample.writeoff_borr ?? []).map((row, index) => ({
      ...row,
      fg113_01: '',
      fg113_02: index + 1,
      fg113_90: 0,
    }));
    const draftWriteoffAdv2 = (sample.writeoff_adv2 ?? []).map((row, index) => ({
      ...row,
      fg114_01: '',
      fg114_02: index + 1,
      fg114_90: 0,
      fg114_91: '',
      fg114_92: null,
      fg114_93: '',
    }));
    const draftAllocFee = (sample.alloc_fee ?? []).map((row, index) => ({
      ...row,
      fg115_01: '',
      fg115_02: index + 1,
    }));

    setRecord(null);
    setLastApiResponse(sample);
    setFormData(draft);
    setDetails(draftDetails);
    setWriteoffAdv(draftWriteoffAdv);
    setWriteoffBorr(draftWriteoffBorr);
    setWriteoffAdv2(draftWriteoffAdv2);
    setAllocFee(draftAllocFee);
    setValidationErrors([]);
  }, []);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ?? []);
      setWriteoffAdv(record.writeoff_adv ?? []);
      setWriteoffBorr(record.writeoff_borr ?? []);
      setWriteoffAdv2(record.writeoff_adv2 ?? []);
      setAllocFee(record.alloc_fee ?? []);
    }
    setValidationErrors([]);
  }, [record]);

  const handleSave = useCallback(
    async (onSuccess: (id: string) => void, onExitEdit: () => void) => {
      const clientErrors = validatePWG201Client(formData, details, writeoffBorr, writeoffAdv2, allocFee);
      if (clientErrors.length > 0) {
        setValidationErrors(clientErrors);
        message.error('資料驗證失敗，請檢查欄位');
        return;
      }
      setLoading(true);
      setValidationErrors([]);
      try {
        const req = {
          master: formData,
          details,
          writeoff_adv: writeoffAdv,
          writeoff_borr: writeoffBorr,
          writeoff_adv2: writeoffAdv2,
          alloc_fee: allocFee,
        };
        let saved: FG110M;
        if (record) {
          saved = await pwg201Api.update(record.fg110_01, req);
          message.success('存檔成功');
        } else {
          saved = await pwg201Api.create(req);
          message.success('新增成功');
        }
        setRecord(saved);
        setLastApiResponse(saved);
        setFormData({ ...saved });
        setDetails(saved.details ?? []);
        setWriteoffAdv(saved.writeoff_adv ?? []);
        setWriteoffBorr(saved.writeoff_borr ?? []);
        setWriteoffAdv2(saved.writeoff_adv2 ?? []);
        setAllocFee(saved.alloc_fee ?? []);
        onExitEdit();
        onSuccess(saved.fg110_01);
      } catch (err: any) {
        if (err?.response?.data?.errors) {
          setValidationErrors(err.response.data.errors);
          message.error('資料驗證失敗，請檢查欄位');
        } else {
          message.error('存檔失敗：' + (err?.message ?? '未知錯誤'));
        }
      } finally {
        setLoading(false);
      }
    },
    [formData, details, writeoffAdv, writeoffBorr, writeoffAdv2, allocFee, record]
  );

  const handleDelete = useCallback(
    async (onSuccess: () => void) => {
      if (!record) return;
      setLoading(true);
      try {
        await pwg201Api.delete(record.fg110_01);
        message.success('刪除成功');
        setRecord(null);
        setFormData(emptyMaster());
        setDetails([]);
        setWriteoffAdv([]);
        setWriteoffBorr([]);
        setWriteoffAdv2([]);
        setAllocFee([]);
        onSuccess();
      } catch (err: any) {
        message.error('刪除失敗：' + (err?.message ?? '未知錯誤'));
      } finally {
        setLoading(false);
      }
    },
    [record]
  );

  const getField = useCallback(
    (field: keyof FG110M) => (formData as any)[field] ?? '',
    [formData]
  );

  const setField = useCallback(
    (field: keyof FG110M, value: any) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  return {
    record,
    lastApiResponse,
    formData,
    details,
    writeoffAdv,
    writeoffBorr,
    writeoffAdv2,
    allocFee,
    loading,
    validationErrors,
    loadRecord,
    resetForAdd,
    loadAcceptanceDraft,
    revertToRecord,
    handleSave,
    handleDelete,
    getField,
    setField,
    setDetails,
    setWriteoffAdv,
    setWriteoffBorr,
    setWriteoffAdv2,
    setAllocFee,
  };
}
